import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Search,
  Calculator,
  Menu,
  X,
  ChevronDown
} from 'lucide-react';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [calcDropdownOpen, setCalcDropdownOpen] = useState(false);
  const location = useLocation();

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
    setCalcDropdownOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  // Auto-close menu on location change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Handle ESC key to close mobile menu
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Prevent background scrolling while menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <header id="header-root" className="sticky top-0 z-50 w-full">
        {/* Background layer isolated from logo rendering stack to prevent subpixel smoothing loss */}
        <div 
          id="header-blur-bg" 
          className="absolute inset-0 border-b border-slate-200/80 bg-white/95 backdrop-blur-md pointer-events-none"
          style={{
            transform: 'none',
            filter: 'none',
          }}
        />
        
        {/* Crisp foreground content layer */}
        <div className="relative z-10 mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          
          {/* LOGO */}
          <Link id="logo-link" to="/" className="flex items-center justify-start" onClick={handleLinkClick} style={{ transform: 'none' }}>
            <img
              id="header-logo-img"
              src="/assets/branding/finsahaj-logo.webp"
              alt="Finsahaj"
              className="block h-auto w-auto max-h-14 lg:max-h-16 object-contain"
              width={220}
              height={70}
              decoding="async"
              fetchPriority="high"
              loading="eager"
              style={{
                width: 'auto',
                height: 'auto',
                maxHeight: '56px',
                objectFit: 'contain',
                transform: 'none',
                filter: 'none',
                backdropFilter: 'none',
                mixBlendMode: 'normal',
                opacity: 1,
              }}
            />
          </Link>

        {/* DESKTOP NAV */}
        <nav id="desktop-nav" className="hidden lg:flex items-center lg:space-x-3 xl:space-x-5 2xl:space-x-8">
          <Link
            id="nav-home"
            to="/"
            onClick={handleLinkClick}
            className={`font-sans lg:text-xs xl:text-sm font-medium transition-colors hover:text-brand-blue whitespace-nowrap ${
              isActive('/') ? 'text-brand-blue' : 'text-slate-600'
            }`}
          >
            Home
          </Link>
          
          <Link
            id="nav-search"
            to="/search"
            onClick={handleLinkClick}
            className={`flex items-center space-x-1 font-sans lg:text-xs xl:text-sm font-medium transition-colors hover:text-brand-blue whitespace-nowrap ${
              isActive('/search') ? 'text-brand-blue' : 'text-slate-600'
            }`}
          >
            <Search className="h-4 w-4 hidden xl:block shrink-0" />
            <span>IFSC Search</span>
          </Link>

          <Link
            id="nav-ifsc-dir"
            to="/ifsc"
            onClick={handleLinkClick}
            className={`font-sans lg:text-xs xl:text-sm font-medium transition-colors hover:text-brand-blue whitespace-nowrap ${
              isActive('/ifsc') ? 'text-brand-blue' : 'text-slate-600'
            }`}
          >
            IFSC Directory
          </Link>

          <Link
            id="nav-bank-dir"
            to="/bank"
            onClick={handleLinkClick}
            className={`font-sans lg:text-xs xl:text-sm font-medium transition-colors hover:text-brand-blue whitespace-nowrap ${
              isActive('/bank') ? 'text-brand-blue' : 'text-slate-600'
            }`}
          >
            Banks
          </Link>

          <Link
            id="nav-state-dir"
            to="/state"
            onClick={handleLinkClick}
            className={`font-sans lg:text-xs xl:text-sm font-medium transition-colors hover:text-brand-blue whitespace-nowrap ${
              isActive('/state') ? 'text-brand-blue' : 'text-slate-600'
            }`}
          >
            States
          </Link>

          <Link
            id="nav-guides"
            to="/guides"
            onClick={handleLinkClick}
            className={`font-sans lg:text-xs xl:text-sm font-medium transition-colors hover:text-brand-blue whitespace-nowrap ${
              isActive('/guides') ? 'text-brand-blue' : 'text-slate-600'
            }`}
          >
            Guides
          </Link>

          {/* CALCULATORS DROPDOWN */}
          <div className="relative">
            <button
              id="nav-calcs-btn"
              onClick={() => setCalcDropdownOpen(!calcDropdownOpen)}
              onMouseEnter={() => setCalcDropdownOpen(true)}
              className={`flex items-center space-x-1 font-sans lg:text-xs xl:text-sm font-medium transition-colors hover:text-brand-blue text-slate-600 focus:outline-none whitespace-nowrap`}
            >
              <Calculator className="h-4 w-4 hidden xl:block shrink-0" />
              <span>Calculators</span>
              <ChevronDown className="h-4 w-4 shrink-0" />
            </button>

            {calcDropdownOpen && (
              <div
                id="calcs-dropdown"
                onMouseLeave={() => setCalcDropdownOpen(false)}
                className="absolute right-0 mt-2 w-56 rounded-xl border border-slate-100 bg-white p-2 shadow-xl ring-1 ring-slate-900/5 focus:outline-none"
              >
                <Link
                  id="calc-hub-link"
                  to="/calculators"
                  onClick={handleLinkClick}
                  className="block rounded-lg px-4 py-2.5 text-sm font-bold text-brand-blue bg-blue-surface hover:bg-blue-surface/80 transition-colors mb-1"
                >
                  Calculators Hub
                </Link>
                <Link
                  id="calc-emi-link"
                  to="/calculators/emi"
                  onClick={handleLinkClick}
                  className="block rounded-lg px-4 py-2.5 text-sm text-slate-700 hover:bg-blue-surface hover:text-brand-blue"
                >
                  EMI Calculator
                </Link>
                <Link
                  id="calc-sip-link"
                  to="/calculators/sip"
                  onClick={handleLinkClick}
                  className="block rounded-lg px-4 py-2.5 text-sm text-slate-700 hover:bg-blue-surface hover:text-brand-blue"
                >
                  SIP Calculator
                </Link>
                <Link
                  id="calc-fd-link"
                  to="/calculators/fd"
                  onClick={handleLinkClick}
                  className="block rounded-lg px-4 py-2.5 text-sm text-slate-700 hover:bg-blue-surface hover:text-brand-blue"
                >
                  FD Calculator
                </Link>
                <Link
                  id="calc-gst-link"
                  to="/calculators/gst"
                  onClick={handleLinkClick}
                  className="block rounded-lg px-4 py-2.5 text-sm text-slate-700 hover:bg-blue-surface hover:text-brand-blue"
                >
                  GST Calculator
                </Link>
              </div>
            )}
          </div>
        </nav>

        {/* CONNECTION STATUS & MOBILE BTN */}
        <div className="flex items-center space-x-4">
          {/* MOBILE TOGGLE */}
          <button
            id="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 hover:text-main-text lg:hidden"
            aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu-panel"
            title={mobileMenuOpen ? "Close Menu" : "Open Menu"}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
    </header>

    {/* MOBILE NAV PANEL - RENDERED OUTSIDE OF BACKDROP-BLUR CONTAINER FOR TRUE VIEWPORT FIXED POSITIONING */}
      {mobileMenuOpen && (
        <>
          {/* Full visual overlay */}
          <div
            id="mobile-menu-backdrop"
            className="fixed inset-0 z-100 bg-slate-950/60 backdrop-blur-sm lg:hidden h-screen w-screen animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div
            id="mobile-menu-panel"
            className="fixed right-0 top-0 bottom-0 z-110 w-[82vw] max-w-[320px] h-screen bg-white p-5 shadow-2xl lg:hidden flex flex-col space-y-4 overflow-y-auto animate-slide-in-right"
          >
            {/* Drawer Header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-1">
              <span className="font-sans text-sm font-black uppercase tracking-wider text-slate-650">Menu</span>
              <button
                id="close-mobile-menu-btn"
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-lg p-1.5 text-slate-700 hover:bg-slate-100 transition-colors font-bold"
                aria-label="Close navigation menu"
                title="Close Menu"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-col space-y-1">
              <Link
                id="mobile-nav-home"
                to="/"
                onClick={handleLinkClick}
                className={`block rounded-lg px-3 py-1.5 text-sm font-semibold transition-all ${
                  isActive('/') ? 'bg-blue-surface text-brand-blue' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                Home
              </Link>
              <Link
                id="mobile-nav-search"
                to="/search"
                onClick={handleLinkClick}
                className={`block rounded-lg px-3 py-1.5 text-sm font-semibold transition-all ${
                  isActive('/search') ? 'bg-blue-surface text-brand-blue' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                IFSC Search
              </Link>
              <Link
                id="mobile-nav-ifsc"
                to="/ifsc"
                onClick={handleLinkClick}
                className={`block rounded-lg px-3 py-1.5 text-sm font-semibold transition-all ${
                  isActive('/ifsc') ? 'bg-blue-surface text-brand-blue' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                IFSC Directory
              </Link>
              <Link
                id="mobile-nav-bank"
                to="/bank"
                onClick={handleLinkClick}
                className={`block rounded-lg px-3 py-1.5 text-sm font-semibold transition-all ${
                  isActive('/bank') ? 'bg-blue-surface text-brand-blue' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                Banks
              </Link>
              <Link
                id="mobile-nav-state"
                to="/state"
                onClick={handleLinkClick}
                className={`block rounded-lg px-3 py-1.5 text-sm font-semibold transition-all ${
                  isActive('/state') ? 'bg-blue-surface text-brand-blue' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                States
              </Link>
              <Link
                id="mobile-nav-guides"
                to="/guides"
                onClick={handleLinkClick}
                className={`block rounded-lg px-3 py-1.5 text-sm font-semibold transition-all ${
                  isActive('/guides') ? 'bg-blue-surface text-brand-blue' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                Guides
              </Link>
              <Link
                id="mobile-nav-calculators-hub"
                to="/calculators"
                onClick={handleLinkClick}
                className={`block rounded-lg px-3 py-1.5 text-sm font-semibold transition-all ${
                  isActive('/calculators') ? 'bg-blue-surface text-brand-blue' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                Calculators Hub
              </Link>
            </div>

            <div className="border-t border-slate-100 pt-3">
              <div className="px-3 py-1 text-[10px] font-black uppercase tracking-wider text-slate-650">
                Calculators
              </div>
              <div className="mt-1 space-y-0.5">
                <Link
                  id="mobile-nav-emi"
                  to="/calculators/emi"
                  onClick={handleLinkClick}
                  className="block rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all"
                >
                  EMI Calculator
                </Link>
                <Link
                  id="mobile-nav-sip"
                  to="/calculators/sip"
                  onClick={handleLinkClick}
                  className="block rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all"
                >
                  SIP Calculator
                </Link>
                <Link
                  id="mobile-nav-fd"
                  to="/calculators/fd"
                  onClick={handleLinkClick}
                  className="block rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all"
                >
                  FD Calculator
                </Link>
                <Link
                  id="mobile-nav-gst"
                  to="/calculators/gst"
                  onClick={handleLinkClick}
                  className="block rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all"
                >
                  GST Calculator
                </Link>
              </div>
            </div>


          </div>
        </>
      )}
  </>
);
}
